import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { functions } from '../functions/_functions';
import { withTheme } from '../core/theming';
import { PropTypesRepo } from '../functions/_propTypes';

/* eslint-disable react/prop-types */

const CustomTag = React.forwardRef((props, ref) => {
  const {
    d,
    pos,
    top,
    bottom,
    left,
    right,
    h,
    maxH,
    minH,
    justify,
    align,
    flexDir,
    flexGrow,
    flexWrap,
    order,
    rounded,
    m,
    p,
    bg,
    hoverBg,
    textColor,
    textWeight,
    textAlign,
    textTransform,
    textDecor,
    textSize,
    transition,
    w,
    maxW,
    minW,
    fontFamily,
    opacity,
    hoverTextColor,
    tag,
    children,
    theme,
    ...rest
  } = props;
  return React.createElement(`${tag}`, { ...rest, ref }, children);
});

/* eslint-enable react/prop-types */

/**
 * Style the Text
 */
const TextStyled = styled(CustomTag)`
  margin: 0;
  padding: 0;
  ${(props) => props.d && functions.makeResponsive('display', props.d)};
  ${(props) => props.pos && functions.makeResponsive('position', props.pos)};
  ${(props) => props.top && functions.makeResponsive('top', props.top)};
  ${(props) => props.bottom && functions.makeResponsive('bottom', props.bottom)};
  ${(props) => props.left && functions.makeResponsive('left', props.left)};
  ${(props) => props.right && functions.makeResponsive('right', props.right)};
  ${(props) => props.h && functions.makeResponsive('height', props.h)};
  ${(props) => props.maxH && functions.makeResponsive('max-height', props.maxH)};
  ${(props) => props.minH && functions.makeResponsive('min-height', props.minH)};
  ${(props) => props.w && functions.makeResponsive('width', props.w)};
  ${(props) => props.maxW && functions.makeResponsive('max-width', props.maxW)};
  ${(props) => props.minW && functions.makeResponsive('min-width', props.minW)};
  ${(props) => props.justify && functions.makeResponsive('justify-content', props.justify)};
  ${(props) => props.align && functions.makeResponsive('align-items', props.align)};
  ${(props) => props.flexDir && functions.makeResponsive('flex-direction', props.flexDir)};
  ${(props) => props.flexGrow && functions.makeResponsive('flex', props.flexGrow)};
  ${(props) => props.flexWrap && functions.makeResponsive('flex-wrap', props.flexWrap)};
  ${(props) => props.rounded && functions.findRoundedDirection(props.rounded, props.theme.rounded)};
  ${(props) => props.m && functions.findDirection('margin', props.m)};
  ${(props) => props.p && functions.findDirection('padding', props.p)};
  ${(props) => props.cursor && `cursor: ${props.cursor};`};
  ${(props) => props.bg && functions.makeResponsiveProperty(props.bg, 'background', props.theme.colors)};
  ${(props) => props.textSize && functions.makeResponsiveFontSize(props.textSize, props.theme.textSize)};
  ${(props) => props.textColor && functions.makeResponsive('color', props.theme.colors[props.textColor])};
  ${(props) => props.textWeight && functions.makeResponsive('font-weight', props.textWeight)};
  ${(props) => props.textAlign && functions.makeResponsive('text-align', props.textAlign)};
  ${(props) => props.verticalAlign && functions.makeResponsive('vertical-align', props.verticalAlign)};
  ${(props) => props.textTransform && functions.makeResponsive('text-transform', props.textTransform)};
  ${(props) => props.textDecor && functions.makeResponsive('text-decoration', props.textDecor)};
  ${(props) => props.transition && `transition: ${props.theme.transition}`};
  ${(props) => props.order && functions.makeResponsive('order', props.order)};
  ${(props) => props.fontFamily && `font-family: ${props.theme.fontFamily[props.fontFamily]}`};
  ${(props) => props.opacity && functions.makeResponsive('opacity', props.opacity)};

  :visited {
    ${(props) => props.textColor && functions.makeResponsive('color', props.theme.colors[props.textColor])};
  }

  :hover {
    ${(props) => props.hoverBg && functions.makeResponsiveProperty(props.hoverBg, 'background', props.theme.colors)};
    ${(props) => props.hoverTextColor && functions.makeResponsive('color', props.theme.colors[props.hoverTextColor])};
  }
`;

const Text = (props) => <TextStyled {...props} />;

/* eslint-disable react/require-default-props */

Text.propTypes = {
  tag: PropTypes.string,
  d: PropTypesRepo.d,
  pos: PropTypesRepo.pos,
  top: PropTypesRepo.top,
  bottom: PropTypesRepo.bottom,
  left: PropTypesRepo.left,
  right: PropTypesRepo.right,
  h: PropTypesRepo.h,
  maxH: PropTypesRepo.maxH,
  minH: PropTypesRepo.minH,
  w: PropTypesRepo.w,
  maxW: PropTypesRepo.maxW,
  minW: PropTypesRepo.minW,
  justify: PropTypesRepo.justify,
  align: PropTypesRepo.align,
  order: PropTypesRepo.order,
  flexDir: PropTypesRepo.flexDir,
  flexGrow: PropTypesRepo.flexGrow,
  flexWrap: PropTypesRepo.flexWrap,
  rounded: PropTypesRepo.rounded,
  m: PropTypesRepo.m,
  p: PropTypesRepo.p,
  cursor: PropTypesRepo.cursor,
  bg: PropTypesRepo.bg,
  textSize: PropTypesRepo.textSize,
  textColor: PropTypesRepo.textColor,
  textWeight: PropTypesRepo.textWeight,
  textAlign: PropTypesRepo.textAlign,
  textTransform: PropTypesRepo.textTransform,
  textDecor: PropTypesRepo.textDecor,
  verticalAlign: PropTypesRepo.verticalAlign,
  fontFamily: PropTypesRepo.fontFamily,
  transition: PropTypesRepo.transition,
  opacity: PropTypesRepo.opacity,
  hoverBg: PropTypesRepo.hoverBg,
  hoverTextColor: PropTypesRepo.hoverTextColor,
};

/* eslint-enable react/require-default-props */

Text.defaultProps = {
  tag: 'p',
  fontFamily: 'primary',
};

export default withTheme(Text);
